import { IInstrumentSettlementDetails } from '@phonepe/galactus';

import { IApiResponse } from '@shared/types';

import { TRANSACTION_SOLUTIONS } from '@/constants';

import { IOnlineTransactionDetails } from './OnlineTransactions.types';
import { IChatBotTokenResponse } from './ChatBot.types';

export type TValueType = string | number | JSX.Element | null;

export enum TransactionType {
	FORWARD = 'FORWARD',
	REFUND = 'REFUND',
	NCMC = 'NCMC',
	MERCHANT_INCENTIVE = 'MERCHANT_INCENTIVE',
	CASHBACK = 'CASHBACK',
	EXPRESS_BUY = 'EXPRESS_BUY',
	CHARGE = 'CHARGE',
	PAYLINK_META_ENTITY = 'PAYLINK_META_ENTITY',
	// NOTE: Need to confirm with backend below TRANSACTION_TYPES exists or not
	CASHBACK_TRANSACTION = 'CASHBACK_TRANSACTION',
	PAYLINK = 'PAYLINK',
	ONLINE_ORDER = 'ONLINE_ORDER',
}

export enum TransactionStatus {
	COMPLETED = 'COMPLETED',
	PENDING = 'PENDING',
	ERRORED = 'ERRORED',
	CANCELLED = 'CANCELLED',
	FAILED = 'FAILED',
	PROCESSING = 'PROCESSING',
	QUEUED = 'QUEUED',
	ACTIVE = 'ACTIVE',
	DEEMED = 'DEEMED',
	INITIATED = 'INITIATED',
	APPROVED = 'APPROVED',
	SUCCESS = 'SUCCESS',
}

export type TTransactionTypes =
	(typeof TransactionType)[keyof typeof TransactionType];

export type TTransactionStatus =
	(typeof TransactionStatus)[keyof typeof TransactionStatus];

export interface ISubscriptionData {
	key: string;
	value: TValueType;
	infoText: string;
	showOnEmpty: boolean;
	isCopy: boolean;
}

export interface ITransactionOverviewData {
	key: number;
	icon: JSX.Element;
	title: string;
	value: string;
}

export interface ITransactionChargeConfig {
	key: string;
	label: string;
	icon?: string;
}

export interface ITransactionCharge extends ITransactionChargeConfig {
	value: number;
}

export type TransactionStatusType = 'COMPLETED' | 'PENDING' | 'ERRORED';

export interface IInstrumentDetails {
	instrumentIdentifierKey: string;
	instrumentType: string;
	amount: number;
	displayName: string;
	arn: string;
	brn: string;
	bankId: string;
	authCode: string;
	utr: string;
	cardIssuer?: string;
	processingRail: string;
	vpa: string;
	mode: string;
	createdAt: number;
	omTransactionId: string;
}

export interface ITransactionMerchantDetails {
	merchantId: string;
	storeId: string;
	terminalId: string;
	storeName: string;
	terminalName: string;
	qrCodeId: string;
	merchantType: string;
}

export interface ITransactionCustomerDetails {
	userId: string;
	phoneNumber: string;
	userName: string;
}

export interface ITransactionPaymentApp {
	paymentApp: string;
	logo: string;
	displayText: string;
}

export interface ITransactionSettlement {
	utr: string;
	status: string;
	settlementDate: number;
	settlementAmount: number;
}

export interface ITransactionSettlementDetails {
	status: string;
	toBeSettledDate: number;
	settlementList: ITransactionSettlement[];
}

export interface ITransactionAttempt {
	amount: number;
	createdAt: number;
	paymentTransactionId: string;
	omTransactionId: string;
	state: TTransactionStatus;
	merchantTransactionId: string;
	paymentState: TTransactionStatus;
	transactionDate: number;
	providerReferenceId: string;
	transactionId: string;
	errorCode: string;
	errorMessage?: string;
	completedAt: number;
	autoFailed: boolean;
	transactionNote: string;
	utr: string;
	arn: string;
	instrumentDetails: IInstrumentDetails[];
}

export interface IRefundTransactionPayload {
	transactionId: string;
	message: string;
	amount: number;
	merchantId: string;
	providerReferenceId: string;
	merchantOrderId: string;
}

export interface ITransactionDetailParams {
	params: {
		transactionType?: string;
	};
}
export type TTransactionSolutions =
	(typeof TRANSACTION_SOLUTIONS)[keyof typeof TRANSACTION_SOLUTIONS]['value'];

export interface IBaseTransactionDetails {
	unitId: string;
	merchantId: string;
	transactionId: string;
	transactionType: TTransactionTypes;
	paymentState: TTransactionStatus;
	amount: number;
	merchantTransactionId: string;
	merchantOrderId?: string;
	providerReferenceId: string;
	instrumentDetails: IInstrumentDetails[];
	transactionDate: number;
	merchantDetails: ITransactionMerchantDetails;
	customerDetails: ITransactionCustomerDetails;
	paymentApp: ITransactionPaymentApp;
	utr: string;
	payResponseCode: string;
	globalPaymentId: string;
	offerAdjustment: number;
	collectPaymentTransactionId: string;
	isExternalVPATransaction: boolean;
	vpa: string;
	cashbackApplied: boolean;
	instrumentLevelSettlementDetails: Record<
		string,
		IInstrumentSettlementDetails
	>;
	settlement: ITransactionSettlementDetails;
	vasDetails: {
		displayName: string;
	}[];
	additionalTransactionDetails: ITransactionAttempt[];
	solutionType: TTransactionSolutions;
	solutionName: string;
	externalVPATransaction?: boolean;
	autoFailed?: boolean;
}

export interface IRefundTransaction extends Partial<IBaseTransactionDetails> {
	merchantId: string;
	transactionId: string;
	transactionType: TTransactionTypes;
	paymentState: TTransactionStatus;
	amount: number;
	merchantTransactionId?: string;
	providerReferenceId?: string;
	instrumentDetails: IInstrumentDetails[];
	transactionDate: number;
	merchantDetails: ITransactionMerchantDetails;
	customerDetails: ITransactionCustomerDetails;
	payResponseCode: string;
	offerAdjustment: number;
	isExternalVPATransaction: boolean;
	originalTransactionId: string;
	vpa: string;
	cashbackApplied: boolean;
	externalVPATransaction: boolean;
	utr: string;
	arn: string;
	transactionNote: string;
	code: string;
	errorMessage?: string;
}

export interface IOfflineTransactionDetails extends IBaseTransactionDetails {
	refundTransactions: IRefundTransaction[];
	transactionNote: string;
	productFlowType: string;
	phonepeSubscriptionId: string;
	merchantSubscriptionId: string;
}

export type TTransactionDetailsData = IOfflineTransactionDetails &
	IOnlineTransactionDetails;

export interface ITransactionDetailsResponse<
	T = IOfflineTransactionDetails | IOnlineTransactionDetails,
> {
	data: { data: T };
	success: boolean;
}

export interface ITransactionOverviewItem {
	key: number;
	icon: JSX.Element;
	title: string;
	value: TValueType;
}

export interface ITransactionCardData {
	amount: string;
	date: string;
	methodName: string;
	paymentApp: ITransactionPaymentApp;
	paymentState: TTransactionStatus;
}

export interface ISettlementCardData {
	status: string;
	utr?: string;
	settlementDate?: string;
}

export interface IOverviewData {
	payeeName: string;
	payerName: string;
	transactionOverviewData: ITransactionOverviewItem[];
	transactionData: ISubscriptionData[];
	transactionCardData: ITransactionCardData;
	settlementCardData: ITransactionSettlement;
}

export interface IFailedPayment {
	date: number;
	time?: number;
	txnNumber?: string;
	failureReason?: string;
}

export interface ICashbackMetaData {
	transactionId?: string;
	merchantId?: string;
	userId?: string;
}

export interface ITransactionDetailsCashbackPayload extends ICashbackMetaData {
	transactionTypeFilters: string[];
}

export interface IRefundParsedData {
	date: number;
	time: number;
	transactionId: string;
	merchantTransactionId: string;
	providerReferenceId: string | undefined;
	transactionNote: string;
	utrNumber: string;
	arnNumber: string;
	amount: string;
	instrument: JSX.Element | null;
	paymentState: TTransactionStatus;
}

export type IRefundTransactionPick = Pick<
	IRefundTransaction,
	'amount' | 'offerAdjustment' | 'paymentState'
>;

export interface ITransactionDetailsConfig<T = TTransactionDetailsData> {
	getTransactionDetailByUnitId: (
		unitId: string,
		data: ITransactionDetailParams,
	) => Promise<IApiResponse<ITransactionDetailsResponse<T>>>;
	getTransactionDetail: (
		payload: ITransactionDetailsCashbackPayload,
	) => Promise<IApiResponse<ITransactionDetailsResponse<T>>>;
	refundTransaction: (
		payload: IRefundTransactionPayload,
	) => Promise<IApiResponse<IRefundTransaction>>;
	refreshToken: () => Promise<void>;
	getChatBotToken: () => Promise<IApiResponse<IChatBotTokenResponse>>;
	transformTransactionDetailsData: (
		res: ITransactionDetailsResponse<T>,
		chimeraConfigData?: Record<string, string>,
	) => TTransactionDetailsData;
	transactionOverviewDataParser: ({
		data,
		isNcmc,
		transactionChargesConfig,
	}: {
		data: TTransactionDetailsData;
		isNcmc?: boolean;
		transactionChargesConfig?: T[];
	}) => IOverviewData;
	transactionCashbackApplied: (data: T) => boolean;
	transactionFailedPaymentsDataParser: (
		data: ITransactionAttempt[],
	) => IFailedPayment[];
	transactionRefundsDataParser?: (
		data: IRefundTransaction[],
	) => IRefundParsedData[];
	getCashbackMetaData: (data: T) => ICashbackMetaData;
	getSubscriptionHeader?: (data: T) => string;
	transactionSubscriptionDataParser?: (data: T) => ISubscriptionData[];
	visibility: {
		detailsCard: {
			header: boolean;
		};
	};
}
